import type {
  LeDataChatMessage,
  LeDataContentBlock,
  LeDataContentType,
  LeDataFeedbackRating,
  LeDataProvenance,
  LeDataUsageSummary,
} from '~/types/leData';

import { parseLeDataProvenance } from '~/utils/leData/provenance';

import type { AskFailureCode, AskUIMessage, AskUIMessagePart } from './askUiMessage';
import { imageAttachmentOf, userImageParts } from './imageParts';

export type ToolStepState = 'running' | 'complete' | 'error';

export interface ToolStep {
  kind: 'tool';
  id: string;
  toolName: string;
  label: string;
  state: ToolStepState;
  latencyMs?: number;
  errorText?: string;
  input?: unknown;
}

export interface ThoughtStep {
  kind: 'thought';
  id: string;
  text: string;
  streaming: boolean;
}

export type TimelineStep = ToolStep | ThoughtStep;

export interface AssistantTurn {
  id: string;
  blocks: Array<LeDataContentBlock>;
  timeline: Array<TimelineStep>;
  steers: Array<string>;
  provenance?: LeDataProvenance;
  usage?: LeDataUsageSummary;
  turnUsage?: LeDataUsageSummary;
  sessionId?: string;
  errorText?: string;
  code?: AskFailureCode;
  hasAnswerText: boolean;
  startedAt?: string;
  finishedAt?: string;
  feedbackRating?: LeDataFeedbackRating;
}

const BLOCK_DATA_PARTS = new Set<LeDataContentType>([
  'sql',
  'table',
  'chart',
  'kpi',
  'file',
  'error',
  'lens_scope',
  'lens_draft',
  'lens_filtered',
  'lens_applied',
  'lens_conflict',
  'lens_created',
]);

const DATA_PART_PREFIX = 'data-';

type DataPart = { type: `data-${string}`; id?: string; data: unknown };
type DynamicToolPart = Extract<AskUIMessagePart, { type: 'dynamic-tool' }>;

function isDataPart(part: AskUIMessagePart): part is AskUIMessagePart & DataPart {
  return part.type.startsWith(DATA_PART_PREFIX);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function toolLatency(output: unknown): number | undefined {
  const latency = asRecord(output)?.latencyMs;
  return typeof latency === 'number' && Number.isFinite(latency) ? latency : undefined;
}

function toolStepState(part: DynamicToolPart): ToolStepState {
  if (part.state === 'output-available') {
    return 'complete';
  }
  if (part.state === 'output-error' || part.state === 'output-denied') {
    return 'error';
  }
  return 'running';
}

function toolStep(part: DynamicToolPart): ToolStep {
  return {
    kind: 'tool',
    id: part.toolCallId,
    toolName: part.toolName,
    label: part.title?.trim() ?? '',
    state: toolStepState(part),
    latencyMs: part.state === 'output-available' ? toolLatency(part.output) : undefined,
    errorText: part.state === 'output-error' ? part.errorText : undefined,
    input: part.input,
  };
}

export interface WorkRow {
  id: string;
  label: string;
  state: ToolStepState;
  latencyMs?: number;
}

const STATE_PRECEDENCE: Record<ToolStepState, number> = { complete: 0, error: 1, running: 2 };

function laterState(a: ToolStepState, b: ToolStepState): ToolStepState {
  return STATE_PRECEDENCE[a] >= STATE_PRECEDENCE[b] ? a : b;
}

function addLatency(a?: number, b?: number): number | undefined {
  return a === undefined && b === undefined ? undefined : (a ?? 0) + (b ?? 0);
}

export function businessWorkRows(timeline: ReadonlyArray<TimelineStep>): Array<WorkRow> {
  const rows: Array<WorkRow> = [];
  for (const step of timeline) {
    if (step.kind !== 'tool' || !step.label) {
      continue;
    }
    const last = rows[rows.length - 1];
    if (last?.label === step.label) {
      rows[rows.length - 1] = {
        ...last,
        state: laterState(last.state, step.state),
        latencyMs: addLatency(last.latencyMs, step.latencyMs),
      };
    } else {
      rows.push({ id: step.id, label: step.label, state: step.state, latencyMs: step.latencyMs });
    }
  }
  return rows;
}

export function projectAssistantTurn(message: AskUIMessage): AssistantTurn {
  const turn: AssistantTurn = {
    id: message.id,
    blocks: [],
    timeline: [],
    steers: [],
    usage: message.metadata?.usage,
    turnUsage: message.metadata?.turnUsage,
    sessionId: message.metadata?.sessionId,
    errorText: message.metadata?.errorText,
    code: message.metadata?.code,
    hasAnswerText: false,
    startedAt: message.metadata?.startedAt,
    finishedAt: message.metadata?.finishedAt,
    feedbackRating: message.metadata?.feedbackRating,
  };

  message.parts.forEach((part, index) => {
    const fallbackId = `${message.id}:${index}`;
    if (part.type === 'text') {
      if (!part.text.trim()) {
        return;
      }
      turn.hasAnswerText = true;
      turn.blocks.push({ id: fallbackId, type: 'text', payload: { content: part.text } });
      return;
    }
    if (part.type === 'reasoning') {
      if (!part.text.trim()) {
        return;
      }
      const id = part.id ?? fallbackId;
      turn.timeline.push({ kind: 'thought', id, text: part.text, streaming: part.state === 'streaming' });
      turn.blocks.push({ id, type: 'text', payload: { markdown: part.text, section: 'thought-process' } });
      return;
    }
    if (part.type === 'dynamic-tool') {
      turn.timeline.push(toolStep(part));
      return;
    }
    if (part.type === 'file') {
      turn.blocks.push({
        id: fallbackId,
        type: 'file',
        payload: { url: part.url, name: part.filename, mediaType: part.mediaType },
      });
      return;
    }
    if (!isDataPart(part)) {
      return;
    }

    const name = part.type.slice(DATA_PART_PREFIX.length);
    const data = asRecord(part.data);
    if (!data) {
      return;
    }
    if (name === 'provenance') {
      turn.provenance = parseLeDataProvenance(data) ?? turn.provenance;
      return;
    }
    if (name === 'steered') {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      if (text) {
        turn.steers.push(text);
      }
      return;
    }
    if (BLOCK_DATA_PARTS.has(name as LeDataContentType)) {
      turn.blocks.push({
        id: part.id ?? fallbackId,
        type: name as LeDataContentType,
        payload: data,
      });
    }
  });

  return turn;
}

export function userMessageText(message: AskUIMessage): string {
  return message.parts
    .flatMap((part) => (part.type === 'text' ? [part.text] : []))
    .join('')
    .trim();
}

export function answerMessageOf(turn: AssistantTurn): LeDataChatMessage {
  return {
    id: turn.id,
    role: 'assistant',
    blocks: turn.blocks,
    provenance: turn.provenance,
    traceId: turn.provenance?.traceId,
    feedbackRating: turn.feedbackRating,
  };
}

export function conversationMessages(messages: ReadonlyArray<AskUIMessage>): Array<LeDataChatMessage> {
  return messages.map((message) =>
    message.role === 'assistant'
      ? answerMessageOf(projectAssistantTurn(message))
      : {
          id: message.id,
          role: 'user',
          text: userMessageText(message),
          images: userImageParts(message).flatMap((part) => imageAttachmentOf(part) ?? []),
        },
  );
}

export function latestSessionId(messages: ReadonlyArray<AskUIMessage>): string | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const sessionId = messages[index].metadata?.sessionId;
    if (sessionId) {
      return sessionId;
    }
  }
  return undefined;
}

export function latestLensBlockIds(turns: ReadonlyArray<AssistantTurn>): { scope: string | null } {
  let scope: string | null = null;
  for (const turn of turns) {
    for (const block of turn.blocks) {
      if (block.type === 'lens_scope') {
        scope = block.id;
      }
      if (block.type === 'lens_draft' || block.type === 'lens_applied' || block.type === 'lens_created') {
        scope = null;
      }
    }
  }
  return { scope };
}

export function isMarkdownTableOnly(text: string): boolean {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 1 && lines.every((line) => line.startsWith('|') && line.endsWith('|'));
}

export function withoutTableOnlyProse(blocks: Array<LeDataContentBlock>): Array<LeDataContentBlock> {
  return blocks.filter(
    (block) =>
      !(
        block.type === 'text' &&
        block.payload.section !== 'thought-process' &&
        typeof block.payload.content === 'string' &&
        isMarkdownTableOnly(block.payload.content)
      ),
  );
}
